import { toLatinDigits, toPersianDigits } from './digits.js';
import {
  DISPLAY_TIME_ZONE,
  JALALI_MONTH_NAMES,
  gregorianToJalali,
  isValidJalaliDate,
  jalaliMonthLength,
  jalaliToGregorian,
  wallClock,
} from './jalali.js';

/**
 * Calendar days of the clinic (ADR-027 §6: Asia/Tehran). A `CivilDate` is a day on the wall
 * calendar, independent of time zones; instants are always UTC `Date`s. Converting between the two
 * goes through the time zone database, so a future DST rule would still be handled.
 */
export interface CivilDate {
  year: number;
  month: number;
  day: number;
}

/** Saturday first, as on Iranian calendars. */
export const JALALI_WEEKDAY_NAMES = ['شنبه', 'یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنجشنبه', 'جمعه'] as const;

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** The instant at which the wall clock in `timeZone` shows this day and time. */
export function instantFromWallClock(
  date: CivilDate,
  hour: number,
  minute: number,
  timeZone: string = DISPLAY_TIME_ZONE,
): Date {
  const target = Date.UTC(date.year, date.month - 1, date.day, hour, minute);
  let guess = target;
  // Each step corrects by the offset seen at the current guess; two steps settle any offset change.
  for (let i = 0; i < 3; i += 1) {
    const seen = wallClock(new Date(guess), timeZone);
    const diff = target - Date.UTC(seen.year, seen.month - 1, seen.day, seen.hour, seen.minute);
    if (diff === 0) break;
    guess += diff;
  }
  return new Date(guess);
}

/** The wall-calendar day of an instant in `timeZone`. */
export function civilDateOf(instant: Date, timeZone: string = DISPLAY_TIME_ZONE): CivilDate {
  const w = wallClock(instant, timeZone);
  return { year: w.year, month: w.month, day: w.day };
}

export function addDays(date: CivilDate, days: number): CivilDate {
  const d = new Date(Date.UTC(date.year, date.month - 1, date.day + days));
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() };
}

/** `YYYY-MM-DD` (Gregorian), for URLs and `date` values. */
export function toIsoDate(date: CivilDate): string {
  return `${date.year}-${pad2(date.month)}-${pad2(date.day)}`;
}

export function parseIsoDate(value: string): CivilDate | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const date = { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
  const check = addDays(date, 0);
  return check.year === date.year && check.month === date.month && check.day === date.day ? date : null;
}

/** [start, end) of a wall-calendar day, as instants. */
export function dayRange(date: CivilDate, timeZone: string = DISPLAY_TIME_ZONE): { from: Date; to: Date } {
  return { from: instantFromWallClock(date, 0, 0, timeZone), to: instantFromWallClock(addDays(date, 1), 0, 0, timeZone) };
}

export function weekdayIndex(date: CivilDate): number {
  // getUTCDay: 0 = Sunday … 6 = Saturday → 0 = Saturday … 6 = Friday
  return (new Date(Date.UTC(date.year, date.month - 1, date.day)).getUTCDay() + 1) % 7;
}

/** e.g. `شنبه ۸ مهر ۱۴۰۵`. */
export function formatJalaliLongDate(date: CivilDate): string {
  const j = gregorianToJalali(date.year, date.month, date.day);
  const weekday = JALALI_WEEKDAY_NAMES[weekdayIndex(date)] as string;
  return `${weekday} ${toPersianDigits(j.jd)} ${JALALI_MONTH_NAMES[j.jm - 1] as string} ${toPersianDigits(j.jy)}`;
}

/** A month of the Jalali calendar (what a date picker shows at a time). */
export interface JalaliMonth {
  jy: number;
  jm: number;
}

export function jalaliMonthOf(date: CivilDate): JalaliMonth {
  const j = gregorianToJalali(date.year, date.month, date.day);
  return { jy: j.jy, jm: j.jm };
}

export function addJalaliMonths(month: JalaliMonth, delta: number): JalaliMonth {
  const index = month.jy * 12 + (month.jm - 1) + delta;
  return { jy: Math.floor(index / 12), jm: (((index % 12) + 12) % 12) + 1 };
}

/** The civil date of a day of a Jalali month; `jd` is clamped to the month's length. */
export function civilDateOfJalali(month: JalaliMonth, jd: number): CivilDate {
  const day = Math.min(Math.max(1, jd), jalaliMonthLength(month.jy, month.jm));
  const g = jalaliToGregorian(month.jy, month.jm, day);
  return { year: g.gy, month: g.gm, day: g.gd };
}

/**
 * The days of a Jalali month as civil dates, and the weekday column of its first day
 * (0 = Saturday), which is how many empty cells a month grid starts with.
 */
export function jalaliMonthDays(month: JalaliMonth): { offset: number; days: CivilDate[] } {
  const first = civilDateOfJalali(month, 1);
  const length = jalaliMonthLength(month.jy, month.jm);
  return { offset: weekdayIndex(first), days: Array.from({ length }, (_, i) => addDays(first, i)) };
}

/** e.g. `مهر ۱۴۰۵`. */
export function formatJalaliMonthYear(month: JalaliMonth): string {
  return `${JALALI_MONTH_NAMES[month.jm - 1] as string} ${toPersianDigits(month.jy)}`;
}

/** The Gregorian months as Persian speakers name them (expiry dates are printed Gregorian, BR-04). */
export const GREGORIAN_MONTH_NAMES = [
  'ژانویه',
  'فوریه',
  'مارس',
  'آوریل',
  'مه',
  'ژوئن',
  'ژوئیه',
  'اوت',
  'سپتامبر',
  'اکتبر',
  'نوامبر',
  'دسامبر',
] as const;

/** A month of the Gregorian calendar. */
export interface GregorianMonth {
  year: number;
  month: number;
}

export function gregorianMonthOf(date: CivilDate): GregorianMonth {
  return { year: date.year, month: date.month };
}

export function addGregorianMonths(month: GregorianMonth, delta: number): GregorianMonth {
  const index = month.year * 12 + (month.month - 1) + delta;
  return { year: Math.floor(index / 12), month: (((index % 12) + 12) % 12) + 1 };
}

/** The days of a Gregorian month, with the weekday column of the first one (Saturday = 0). */
export function gregorianMonthDays(month: GregorianMonth): { offset: number; days: CivilDate[] } {
  const first = { year: month.year, month: month.month, day: 1 };
  const length = new Date(Date.UTC(month.year, month.month, 0)).getUTCDate();
  return { offset: weekdayIndex(first), days: Array.from({ length }, (_, i) => addDays(first, i)) };
}

/** e.g. `دوشنبه ۳۱ مه ۲۰۲۷`; without the weekday, `۳۱ مه ۲۰۲۷`. */
export function formatGregorianLongDate(date: CivilDate, weekday = true): string {
  const text = `${toPersianDigits(date.day)} ${GREGORIAN_MONTH_NAMES[date.month - 1] as string} ${toPersianDigits(date.year)}`;
  return weekday ? `${JALALI_WEEKDAY_NAMES[weekdayIndex(date)] as string} ${text}` : text;
}

/** e.g. `مه ۲۰۲۷`. */
export function formatGregorianMonthYear(month: GregorianMonth): string {
  return `${GREGORIAN_MONTH_NAMES[month.month - 1] as string} ${toPersianDigits(month.year)}`;
}

/** Minutes since the wall-clock midnight of the instant's day (for placing it on a day grid). */
export function minutesIntoDay(instant: Date, timeZone: string = DISPLAY_TIME_ZONE): number {
  const w = wallClock(instant, timeZone);
  return w.hour * 60 + w.minute;
}

/**
 * A Jalali date typed by a user (`۱۳۷۰/۰۱/۲۵`, any digits and separators) as Gregorian
 * `YYYY-MM-DD`, or null when it is not a valid Jalali date in 1300..1500.
 */
export function parseJalaliDateInput(value: string): string | null {
  const tokens = toLatinDigits(value).match(/\d+/g);
  if (!tokens || tokens.length !== 3) return null;
  const [jy, jm, jd] = tokens.map((t) => Number.parseInt(t, 10)) as [number, number, number];
  if (!(jy >= 1300 && jy <= 1500) || !isValidJalaliDate(jy, jm, jd)) return null;
  const g = jalaliToGregorian(jy, jm, jd);
  return toIsoDate({ year: g.gy, month: g.gm, day: g.gd });
}
